import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Upload, Search, ArrowRight } from 'lucide-react';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 48px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: #666;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-top: 60px;
`;

const FileItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background:rgb(255, 255, 255);
  border-radius: 12px;
  border-left: 4px solid #d4af37;
  transition: all 0.3s ease;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid #971b2f;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    border-color: #971b2f;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #971b2f 0%, #971b2f 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: white;
`;

const FeatureTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const FeatureDescription = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const FeaturePreview = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
`;

const PreviewTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PreviewContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
`;

const PreviewIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color || '#ffffff'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`;

const PreviewText = styled.div`
  flex: 1;
`;

const PreviewFileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const PreviewFileInfo = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #971b2f 0%,rgb(142, 25, 45) 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(151, 27, 48, 0.43);
  }
`;

const Home = () => {
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <Container>
      <HeroSection>
        <Title>POD Management System</Title>
        <Subtitle>
          Streamline POD document workflows: Upload for International Logistics, Search for Sales teams.
        </Subtitle>
      </HeroSection>

      <FeaturesGrid>
        <FeatureCard
          onClick={() => handleNavigate('/upload')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FeatureIcon>
            <Upload size={32} />
          </FeatureIcon>

          <FeatureTitle>Upload POD</FeatureTitle>
          <FeatureDescription>
            Upload POD PDF files.
            (For International Logistic use only)
          </FeatureDescription>
          <ActionButton>
            <Upload size={20} />
            Upload PODs
            <ArrowRight size={16} />
          </ActionButton>
        </FeatureCard>

        <FeatureCard
          onClick={() => handleNavigate('/download')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FeatureIcon>
            <Search size={32} />
          </FeatureIcon>

          <FeatureTitle>Find POD</FeatureTitle>
          <FeatureDescription>
            Quickly find and download specific POD documents by ID.
          </FeatureDescription>
          <ActionButton>
            <Search size={20} />
            Search PODs
            <ArrowRight size={16} />
          </ActionButton>
        </FeatureCard>
      </FeaturesGrid>
    </Container>
  );
};

export default Home;